import { create } from 'zustand';

export interface CompanyData {
  logo?: any[];
  fotosLocal?: any[];
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  dataFundacao: string;
  site: string;
  instagram: string;
  facebook: string;
  linkedin: string;
  tiktok: string;
  youtube: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  cidade: string;
  estado: string;
  telefonePrincipal: string;
  whatsappPrincipal: string;
  emailPrincipal: string;
  emailFinanceiro: string;
  nichoMercado?: string;
  raioAtuacao?: string;
  horarioFuncionamento?: string;
}

export interface Responsible {
  id: string;
  avatar?: any[];
  area: 'Proprietário' | 'Marketing' | 'Comercial' | string;
  nome: string;
  cargo: string;
  telefone: string;
  whatsapp: string;
  email: string;
}

export interface DocumentItem {
  id: string;
  nome: string;
  descricao: string;
  files: any[];
}

export interface Diferencial {
  id: string;
  titulo: string;
  descricao: string;
}

export interface MotivoEscolha {
  id: string;
  motivo: string;
  descricao: string;
}

export interface BusinessDetails {
  documentos: DocumentItem[];
  oQueFaz: string;
  historia: string;
  missao: string;
  visao: string;
  valores: string;
  diferenciais: Diferencial[];
  porqueEscolhem: MotivoEscolha[];
  porqueNaoConcorrentes: MotivoEscolha[];
  tomDeVoz?: string;
  garantias?: string;
}

export interface Product {
  id: string;
  nome: string;
  descricao: string;
  preco: string;
  ticketMedio: string;
  margemEstimada: string;
  tempoEntrega: string;
  prioridade: string;
  fotos?: any[];
  documentos?: any[];
}

export interface ClientAttribute {
  id: string;
  titulo: string;
  descricao: string;
}

export interface IdealClient {
  sexo: string;
  faixaEtaria: string;
  cidade: string;
  estado: string;
  renda: string;
  dores: ClientAttribute[];
  sonhos: ClientAttribute[];
  objecoes: ClientAttribute[];
  medos: ClientAttribute[];
  desejos: ClientAttribute[];
}

export interface Competitor {
  id: string;
  nome: string;
  site: string;
  instagram: string;
  pontosFortes: ClientAttribute[];
  pontosFracos: ClientAttribute[];
  evidencias?: any[];
}

export interface SalesProcess {
  comoEntra: string;
  quemAtende: string;
  comoQualifica: string;
  comoApresenta: string;
  comoFecha: string;
  ticketMedio: string;
  taxaConversao: string;
  cicloVenda: string;
  numeroVendedores: string;
  formasPagamento?: string;
  scripts?: any[];
  audios?: any[];
  funilPrints?: any[];
}

export interface CRMData {
  usaCrm: boolean;
  qualCrm: string;
  pipelineAtual: string;
  etapasAtuais: string[];
  funilPrints?: any[];
}

export interface FAQ {
  id: string;
  pergunta: string;
  resposta: string;
}

export interface Objection {
  id: string;
  topico: string;
  resposta: string;
}

export interface Automations {
  faqs: FAQ[];
  objecoes: Objection[];
}

export interface Financial {
  planoContratado: string;
  valorMensal: string;
  diaCobranca: string;
  formaPagamento: string;
  responsavelFinanceiro: string;
}

export interface Objectives {
  meses3Faturamento: string;
  meses3FaturamentoAtual: string;
  meses3Leads: string;
  meses3LeadsAtual: string;
  meses3Vendas: string;
  meses3VendasAtual: string;
  meses12Estrategicos: string;
  ticketMedio: string;
  margemLucro: string;
  cacMaximo: string;
  cplIdeal: string;
  metaOtimizacao: string;
  orcamentoMarketing?: string;
}

export interface BrandingData {
  cores: { primary: string; secondary: string; accent: string; background: string };
  tipografias: { titulos: string; textos: string };
  logos: { principal: any; negativa: any; positiva: any; favicon: any };
  brandbook: any[];
}

export interface MediaLibrary {
  fotosEstruturais: any[];
  fotosEquipe: any[];
  fotosProdutos: any[];
  bRolls: any[];
}

export interface SocialProofs {
  printsAvaliacoes: any[];
  ugcVideos: any[];
  estudosCaso: any[];
}

export interface SupportMaterials {
  iscasDigitais: any[];
  catalogos: any[];
}

export interface GeneralDocs {
  contratos: any[];
  planilhas: any[];
  outros: any[];
}

export interface WikiDoc {
  id: string;
  titulo: string;
  conteudo: string;
}

export interface KnowledgeBase {
  conteudoMassivo: string;
  wikiDocs: WikiDoc[];
  documentos: any[];
}

export interface Connections {
  metaConnected: boolean;
  googleConnected: boolean;
  whatsappConnected: boolean;
}

interface ClientStore {
  companyData: CompanyData;
  setCompanyData: (data: Partial<CompanyData>) => void;
  
  responsibles: Responsible[];
  setResponsibles: (data: Responsible[]) => void;
  
  businessDetails: BusinessDetails;
  setBusinessDetails: (data: Partial<BusinessDetails>) => void;

  products: Product[];
  setProducts: (data: Product[]) => void;
  
  idealClient: IdealClient;
  setIdealClient: (data: Partial<IdealClient>) => void;

  competitors: Competitor[];
  setCompetitors: (data: Competitor[]) => void;

  salesProcess: SalesProcess;
  setSalesProcess: (data: Partial<SalesProcess>) => void;

  crmData: CRMData;
  setCrmData: (data: Partial<CRMData>) => void;

  automations: Automations;
  setAutomations: (data: Partial<Automations>) => void;
  
  financial: Financial;
  setFinancial: (data: Partial<Financial>) => void;
  
  objectives: Objectives;
  setObjectives: (data: Partial<Objectives>) => void;

  brandingData: BrandingData;
  setBrandingData: (data: Partial<BrandingData>) => void;

  mediaLibrary: MediaLibrary;
  setMediaLibrary: (data: Partial<MediaLibrary>) => void;

  socialProofs: SocialProofs;
  setSocialProofs: (data: Partial<SocialProofs>) => void;

  supportMaterials: SupportMaterials;
  setSupportMaterials: (data: Partial<SupportMaterials>) => void;

  generalDocs: GeneralDocs;
  setGeneralDocs: (data: Partial<GeneralDocs>) => void;

  knowledgeBase: KnowledgeBase;
  setKnowledgeBase: (data: Partial<KnowledgeBase>) => void;

  connections: Connections;
  setConnections: (data: Partial<Connections>) => void;

  leads: any[];
  appointments: any[];
  setAppointments: (appointments: any[]) => void;
  setLeads: (leads: any[]) => void;
  addLead: (lead: any) => void;
  updateLead: (id: string, updates: any) => void;
  appSettings: any;
  setAppSettings: (data: any) => void;
}

export const useStore = create<ClientStore>((set) => ({
  companyData: {
    razaoSocial: '', nomeFantasia: '', cnpj: '', dataFundacao: '',
    site: '', instagram: '', facebook: '', linkedin: '', tiktok: '', youtube: '',
    cep: '', rua: '', numero: '', complemento: '', cidade: '', estado: '',
    telefonePrincipal: '', whatsappPrincipal: '', emailPrincipal: '', emailFinanceiro: '',
    nichoMercado: '', raioAtuacao: '', horarioFuncionamento: ''
  },
  setCompanyData: (data) => set((state) => ({ companyData: { ...state.companyData, ...data } })),
  
  responsibles: [
    { id: '1', area: 'Proprietário', nome: '', cargo: '', telefone: '', whatsapp: '', email: '' },
    { id: '2', area: 'Marketing', nome: '', cargo: '', telefone: '', whatsapp: '', email: '' },
    { id: '3', area: 'Comercial', nome: '', cargo: '', telefone: '', whatsapp: '', email: '' },
  ],
  setResponsibles: (responsibles) => set(() => ({ responsibles })),
  
  businessDetails: {
    documentos: [],
    oQueFaz: '', historia: '', missao: '', visao: '', valores: '',
    diferenciais: [], porqueEscolhem: [], porqueNaoConcorrentes: [],
    tomDeVoz: '', garantias: ''
  },
  setBusinessDetails: (data) => set((state) => ({ businessDetails: { ...state.businessDetails, ...data } })),

  products: [],
  setProducts: (products) => set(() => ({ products })),
  
  idealClient: {
    sexo: '', faixaEtaria: '25-34', cidade: '', estado: '', renda: '',
    dores: [], sonhos: [], objecoes: [], medos: [], desejos: []
  },
  setIdealClient: (data) => set((state) => ({ idealClient: { ...state.idealClient, ...data } })),

  competitors: [],
  setCompetitors: (competitors) => set(() => ({ competitors })),

  salesProcess: {
    comoEntra: '', quemAtende: '', comoQualifica: '', comoApresenta: '', comoFecha: '',
    ticketMedio: '', taxaConversao: '', cicloVenda: '', numeroVendedores: '',
    formasPagamento: ''
  },
  setSalesProcess: (data) => set((state) => ({ salesProcess: { ...state.salesProcess, ...data } })),

  crmData: {
    usaCrm: false, qualCrm: '', pipelineAtual: '', etapasAtuais: []
  },
  setCrmData: (data) => set((state) => ({ crmData: { ...state.crmData, ...data } })),

  automations: {
    faqs: [], objecoes: []
  },
  setAutomations: (data) => set((state) => ({ automations: { ...state.automations, ...data } })),
  
  financial: {
    planoContratado: '', valorMensal: '', diaCobranca: '', formaPagamento: '', responsavelFinanceiro: ''
  },
  setFinancial: (data) => set((state) => ({ financial: { ...state.financial, ...data } })),
  
  objectives: {
    meses3Faturamento: '', meses3FaturamentoAtual: '', 
    meses3Leads: '', meses3LeadsAtual: '', 
    meses3Vendas: '', meses3VendasAtual: '', 
    meses12Estrategicos: '',
    ticketMedio: '', margemLucro: '', cacMaximo: '', cplIdeal: '', metaOtimizacao: '',
    orcamentoMarketing: ''
  },
  setObjectives: (data) => set((state) => ({ objectives: { ...state.objectives, ...data } })),

  brandingData: {
    cores: { primary: '', secondary: '', accent: '', background: '' }, 
    tipografias: { titulos: '', textos: '' },
    logos: { principal: null, negativa: null, positiva: null, favicon: null },
    brandbook: []
  },
  setBrandingData: (data) => set((state) => ({ brandingData: { ...state.brandingData, ...data } })),

  mediaLibrary: {
    fotosEstruturais: [], fotosEquipe: [], fotosProdutos: [], bRolls: []
  },
  setMediaLibrary: (data) => set((state) => ({ mediaLibrary: { ...state.mediaLibrary, ...data } })),

  socialProofs: {
    printsAvaliacoes: [], ugcVideos: [], estudosCaso: []
  },
  setSocialProofs: (data) => set((state) => ({ socialProofs: { ...state.socialProofs, ...data } })),

  supportMaterials: {
    iscasDigitais: [], catalogos: []
  },
  setSupportMaterials: (data) => set((state) => ({ supportMaterials: { ...state.supportMaterials, ...data } })),

  generalDocs: {
    contratos: [], planilhas: [], outros: []
  },
  setGeneralDocs: (data) => set((state) => ({ generalDocs: { ...state.generalDocs, ...data } })),

  knowledgeBase: {
    conteudoMassivo: '', wikiDocs: [], documentos: []
  },
  setKnowledgeBase: (data) => set((state) => ({ knowledgeBase: { ...state.knowledgeBase, ...data } })),

  connections: {
    metaConnected: false, googleConnected: false, whatsappConnected: false
  },
  setConnections: (data) => set((state) => ({ connections: { ...state.connections, ...data } })),
  leads: [],
  appointments: [],
  setAppointments: (appointments) => set(() => ({ appointments })),
  setLeads: (leads) => set(() => ({ leads })),
  addLead: (lead) => set((state) => {
    const isDuplicate = state.leads.some(l => l.phone === lead.phone && l.id !== lead.id);
    
    // Roleta de Vendedores (Round-Robin)
    let assignedTo = lead.assignedTo;
    let nextIndex = state.appSettings.lastAssignedRepIndex;
    
    const activeReps = state.responsibles.filter(r => r.nome.trim() !== '');
    if (!assignedTo && activeReps.length > 0) {
      nextIndex = (nextIndex + 1) % activeReps.length;
      assignedTo = activeReps[nextIndex].nome;
    }

    return { 
      leads: [...state.leads, { 
        ...lead, 
        isDuplicate,
        assignedTo,
        updatedAt: new Date().toISOString()
      }],
      appSettings: {
        ...state.appSettings,
        totalLeadsGenerated: state.appSettings.totalLeadsGenerated + 1,
        lastAssignedRepIndex: nextIndex
      }
    };
  }),
  updateLead: (id, updates) => set((state) => ({
    leads: state.leads.map(l => l.id === id ? { ...l, ...updates, updatedAt: new Date().toISOString() } : l)
  })),
  appSettings: { lastLoginAt: new Date(Date.now() - 8 * 86400000).toISOString(), clientStatus: 'healthy', totalLeadsGenerated: 1050, lastAssignedRepIndex: -1 },
  setAppSettings: (data) => set((state) => ({ appSettings: { ...state.appSettings, ...data } })),
}));
